import {
	Box,
	SxProps,
	Theme,
	Button,
	Typography,
	ToggleButton,
	FormControl,
	FormLabel,
	Divider,
	IconButton,
	AutocompleteCloseReason,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useState } from "react";
import _ from "lodash";
import { If } from "../../../../common/components/If";
import { primaryColor, textNeutralSecondary } from "../../../../core/theme";
import CustomSelect from "../../../../common/components/CustomSelect";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import { FormikProps } from "formik";
import { QUERY_SETUP_STEP, UNIT } from "../../constants";
import { FieldDataDefQuery } from "../../../../requests_cm/gecoReferentialService/types";
import { PrimaryButton } from "../../../../common/components/CustomButton";
import CustomAutocomplete from "../../../../common/components/CustomAutocomplete";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	StyledPopper,
	StyledToggleButtonGroup,
} from "./QuerySetup.parts";
import { QuerySetpForm } from "../../TimeseriesPage";

const style: { [key: string]: SxProps<Theme> } = {
	addButton: {
		textTransform: "capitalize",
	},
	select: {
		maxWidth: "340px",
		mt: 1,
	},
	removeIcon: {
		color: primaryColor,
		fontSize: 32,
	},
	formLabel: {
		mb: 1,
	},
};

interface QuerySetupProps {
	formik: FormikProps<QuerySetpForm>;
	fielddataDefinitions?: FieldDataDefQuery[];
}

function QuerySetup(props: QuerySetupProps) {
	const { formik, fielddataDefinitions } = props;
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const [selectedQuery, setSelectedQuery] = useState<Set<string>>(
		new Set([])
	);

	const handleAddOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	const [listOfSubQuery, setListOfSubQuery] = useState<
		Record<
			string,
			{
				subqueryName: string;
				source: string;
			}[]
		>
	>({});

	const handleClick = () => {
		formik.handleSubmit();
	};

	const handleAddingNewQuery = (queryName?: string | null) => {
		if (queryName) {
			if (selectedQuery.has(queryName)) {
				setSelectedQuery((prev) => {
					prev.delete(queryName);
					return prev;
				});
				setListOfSubQuery((prev) => {
					delete prev[queryName];
					return prev;
				});
			} else {
				setSelectedQuery((prev) => prev.add(queryName));
				setListOfSubQuery((prev) => ({
					...prev,
					[queryName]: [
						{
							subqueryName: "",
							source: "",
						},
					],
				}));
				formik.setFieldValue("activeQuery", queryName);
				formik.setFieldValue(`queryList.${queryName}`, {
					name: queryName,
					unit: "MW",
					step: "1D",
				});
				window.scrollTo({
					top: document.body.scrollHeight,
					behavior: "smooth",
				});
			}
		}
	};

	const queryNameList: Set<string> = new Set(
		fielddataDefinitions?.map((element) => element.query_name) || []
	);

	const querygroupedByName: Record<string, FieldDataDefQuery[]> = _.groupBy(
		fielddataDefinitions,
		"query_name"
	);

	const handleSelectedQuery =
		(panel: string) =>
		(event: React.SyntheticEvent, newExpanded: boolean) => {
			formik.setFieldValue("activeQuery", newExpanded ? panel : false);
		};

	const handleAddingNewSubQuery = (queryName: string) => {
		setListOfSubQuery((prev) => ({
			...prev,
			[queryName]: Array.isArray(prev[queryName])
				? [...(prev[queryName] || []), { subqueryName: "", source: "" }]
				: [{ subqueryName: "", source: "" }],
		}));
	};
	const handleDeleteSubQuery = (queryName: string) => {
		// remove the value from formik
		formik.setFieldValue(
			`queryList.${queryName}.subquery.${
				listOfSubQuery[queryName]?.length - 1
			}`,
			""
		);
		setListOfSubQuery((prev) => ({
			...prev,
			[queryName]: prev[queryName]?.slice(0, -1),
		}));
	};

	const getListOfSubQuery = (
		queryList: Record<string, FieldDataDefQuery[]>,
		query: string
	) => {
		return new Set(queryList[query].map((el) => el.sub_query));
	};

	const getListOfServiceProvider = (
		queryList: Record<string, FieldDataDefQuery[]>,
		query: string,
		subQuery: string
	): Set<string> => {
		return new Set(
			queryList[query]
				.filter((el) => el.sub_query === subQuery)
				.map((el) => el.serviceprovider_name ?? "")
		);
	};

	return (
		<Box height={"100%"}>
			<Box
				display={"flex"}
				flexDirection={"row"}
				alignItems={"center"}
				justifyContent={"space-between"}
				mb={4}
			>
				<Typography
					variant="h3"
					mb={0}
					color={textNeutralSecondary}
					fontWeight={700}
				>
					{"Query"}
				</Typography>

				<Button
					startIcon={<AddIcon />}
					sx={style.addButton}
					aria-controls={open ? "basic-menu" : undefined}
					aria-haspopup="true"
					aria-expanded={open ? "true" : undefined}
					onClick={handleAddOpen}
					data-testid="add-new-query"
				>
					{"Add"}
				</Button>

				<StyledPopper
					data-testid="query-poppper"
					open={open}
					anchorEl={anchorEl}
					placement="bottom-start"
				>
					<ClickAwayListener onClickAway={handleMenuClose}>
						<div>
							<CustomAutocomplete
								fetchData={() => {}}
								data={[...queryNameList].map((name) => ({
									id: name,
									name: name,
								}))}
								open
								name="subQuerySelector"
								setFieldValue={formik.setFieldValue}
								onClose={(
									event: React.ChangeEvent<{}>,
									reason: AutocompleteCloseReason
								) => {
									if (
										["selectOption", "escape"].includes(
											reason
										)
									) {
										handleMenuClose();
									}
								}}
								onChange={(event, newValue, reason) => {
									if (
										event.type === "keydown" &&
										((event as React.KeyboardEvent).key ===
											"Backspace" ||
											(event as React.KeyboardEvent)
												.key === "Delete") &&
										reason === "removeOption"
									) {
										return;
									}
									handleAddingNewQuery(
										(
											newValue as {
												id: string;
												name: string;
											}
										)?.name
									);
									handleMenuClose();
								}}
							/>
						</div>
					</ClickAwayListener>
				</StyledPopper>
			</Box>
			<If condition={selectedQuery.size > 0}>
				<Box height={"100%"}>
					{[...selectedQuery].map((query) => (
						<Accordion
							key={query}
							expanded={formik.values.activeQuery === query}
							onChange={handleSelectedQuery(query)}
						>
							<AccordionSummary sx={{ p: "20px 20px" }}>
								<QueryStatsIcon />
								<Typography
									component="span"
									variant="h3"
									fontWeight={"700"}
									ml={2}
								>
									{query}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Box
									display={"flex"}
									justifyContent={"space-between"}
									mb={2}
								>
									<FormControl>
										<FormLabel sx={style.formLabel}>
											Step
										</FormLabel>
										<StyledToggleButtonGroup
											value={
												formik.values.queryList?.[query]
													?.step
											}
											exclusive
											onChange={(
												e: React.MouseEvent<HTMLElement>,
												newAlignment: string | null
											) => {
												formik.setFieldValue(
													`queryList.${query}.step`,
													newAlignment
												);
											}}
											aria-label="text alignment"
										>
											{QUERY_SETUP_STEP.map((step) => (
												<ToggleButton
													key={step}
													value={step}
													aria-label={`${step}-step`}
												>
													{step}
												</ToggleButton>
											))}
										</StyledToggleButtonGroup>
									</FormControl>

									<Box component={"span"} maxWidth="88px">
										<FormLabel
											htmlFor="unit"
											sx={style.formLabel}
										>
											Unit
										</FormLabel>
										<CustomSelect
											name="unit"
											sx={style.select}
											value={
												formik.values.queryList?.[query]
													?.unit ?? ""
											}
											onChange={(e) => {
												formik.setFieldValue(
													`queryList.${query}.unit`,
													e.target.value
												);
											}}
											items={UNIT.map((u) => ({
												key: u,
												value: u,
												label: u,
											}))}
										/>
									</Box>
								</Box>

								{query in listOfSubQuery &&
									listOfSubQuery[query]?.map(
										(
											value: {
												subqueryName: string;
												source: string;
											},
											sqIndex: number
										) => (
											<Box key={sqIndex} mb={4}>
												<Divider />
												<Box mt={3}>
													<FormLabel
														sx={style.formLabel}
													>
														Subquery
													</FormLabel>

													<Box
														component={"span"}
														display={"flex"}
														alignItems={"center"}
													>
														<CustomSelect
															data-testid="select-subquery"
															sx={style.select}
															name={`queryList.${query}.subquery.${sqIndex}.subqueryName`}
															value={
																formik.values
																	.queryList?.[
																	query
																]?.subquery?.[
																	sqIndex
																]
																	?.subqueryName ||
																""
															}
															onChange={
																formik.handleChange
															}
															items={[
																...getListOfSubQuery(
																	querygroupedByName,
																	query
																),
															].map(
																(element) => ({
																	key: element,
																	value: element,
																	label: element,
																})
															)}
														/>
														<If
															condition={
																sqIndex ===
																	(listOfSubQuery[
																		query
																	]?.length ??
																		0) -
																		1 &&
																sqIndex > 0
															}
														>
															<IconButton
																aria-label="delete"
																onClick={() =>
																	handleDeleteSubQuery(
																		query
																	)
																}
															>
																<DeleteOutlineIcon
																	sx={
																		style.removeIcon
																	}
																/>
															</IconButton>
														</If>
													</Box>
												</Box>
												<Box mt={2}>
													<FormLabel
														sx={style.formLabel}
													>
														Source
													</FormLabel>
													<CustomSelect
														sx={style.select}
														name={`queryList.${query}.subquery.${sqIndex}.source`}
														value={
															formik.values
																.queryList?.[
																query
															]?.subquery?.[
																sqIndex
															]?.source || ""
														}
														onChange={
															formik.handleChange
														}
														items={[
															...getListOfServiceProvider(
																querygroupedByName,
																query,
																formik.values
																	.queryList?.[
																	query
																]?.subquery?.[
																	sqIndex
																]
																	?.subqueryName ||
																	""
															),
														].map(
															(
																element: string
															) => ({
																key: element,
																value: element,
																label: element,
															})
														)}
													/>
												</Box>
											</Box>
										)
									)}
								<Box
									display={"flex"}
									justifyContent={"flex-end"}
								>
									<Button
										variant="text"
										startIcon={<AddIcon />}
										sx={style.addButton}
										onClick={() =>
											handleAddingNewSubQuery(query)
										}
									>
										Add another subquery
									</Button>
								</Box>
							</AccordionDetails>
						</Accordion>
					))}
				</Box>
				<Box mt={"30px"} display={"flex"} justifyContent={"flex-end"}>
					<PrimaryButton text="Submit" onClick={handleClick} />
				</Box>
			</If>

			<If condition={selectedQuery.size === 0}>
				<Box height={"100%"} display={"flex"} justifyContent={"center"}>
					<Typography paragraph width={"70%"}>
						{
							"Add a Query to configure the time series \n visualisation for selected contract(s)."
						}
					</Typography>
				</Box>
			</If>
		</Box>
	);
}

export default QuerySetup;
